import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Plane, Globe, Clock, Shield, Package, Truck, FileText, Layers } from "lucide-react";
import PublicNav from "../../components/layout/PublicNav";
import QuickInquiryForm from "../../components/QuickInquiryForm";
import Footer, { CONTACT } from "../../components/layout/Footer";
import GlobalFootprintMap from "../../components/GlobalFootprintMap";
import { api, formatError } from "../../lib/api";
import { CalendarDays } from "lucide-react";
import { SERVICES } from "../../data/services";

/* ---------- ticker strip ---------- */
const TICKER_ITEMS = [
  "Air Freight Forwarding",
  "Ocean Freight Forwarding",
  "Door-to-Door Delivery",
  "Customs Clearance",
  "Bonded Warehousing",
  "Express Air Shipments",
  "Time-Critical Logistics (24/7)",
  "Global Partner Network",
  "Import & Export Handling",
  "Consolidation Services",
  "Packaging & Palletization",
  "Priority Cargo Handling",
  "Real-Time Shipment Coordination",
  "Cost-Optimized Routing",
];

const HERO_TICKER_LOGOS = [
  { Icon: Plane, color: "#DC2A1C", label: "Air freight" },
  { Icon: Globe, color: "#0F766E", label: "Ocean routes" },
  { Icon: Truck, color: "#DC2A1C", label: "Pickup and delivery" },
  { Icon: Shield, color: "#1D4ED8", label: "Clearance assurance" },
  { Icon: Shield, color: "#1D4ED8", label: "Bonded warehousing" },
  { Icon: Plane, color: "#DC2A1C", label: "Express air" },
  { Icon: Clock, color: "#EA580C", label: "24/7 response" },
  { Icon: Globe, color: "#0F766E", label: "Global partner network" },
  { Icon: Package, color: "#1D4ED8", label: "Import export handling" },
  { Icon: Package, color: "#1D4ED8", label: "Consolidated cargo" },
  { Icon: Package, color: "#1D4ED8", label: "Packaging support" },
  { Icon: Clock, color: "#EA580C", label: "Priority cargo handling" },
  { Icon: Clock, color: "#EA580C", label: "Real-time coordination" },
  { Icon: Truck, color: "#DC2A1C", label: "Optimized movement" },
];

const CAPABILITIES = [
  { Icon: Plane,    title: "Air Freight Forwarding",          desc: "Fast, reliable global air cargo solutions with priority handling." },
  { Icon: Globe,    title: "Ocean Freight Forwarding",         desc: "Cost-effective sea freight solutions for FCL and LCL shipments." },
  { Icon: Truck,    title: "Door-to-Door Delivery",            desc: "Seamless end-to-end logistics from pickup to final destination." },
  { Icon: FileText, title: "Customs Clearance",                desc: "Efficient handling of documentation and regulatory compliance." },
  { Icon: Package,  title: "Bonded Warehousing",               desc: "Secure storage solutions with duty advantages and flexibility." },
  { Icon: Plane,    title: "Express Air Shipments",            desc: "Time-critical deliveries with expedited transit and handling." },
  { Icon: Clock,    title: "Time-Critical Logistics (24/7)",   desc: "Round-the-clock support for urgent and sensitive shipments." },
  { Icon: Globe,    title: "Global Partner Network",           desc: "Strong international network ensuring smooth worldwide operations." },
  { Icon: Package,  title: "Import & Export Handling",         desc: "Complete management of inbound and outbound cargo flows." },
  { Icon: Layers,   title: "Consolidation Services",           desc: "Optimized cargo grouping to reduce costs and improve efficiency." },
  { Icon: Package,  title: "Packaging & Palletization",        desc: "Professional cargo preparation for safe and secure transit." },
  { Icon: Shield,   title: "Priority Cargo Handling",          desc: "Special attention for high-value or urgent shipments." },
  { Icon: Clock,    title: "Real-Time Shipment Coordination",  desc: "Continuous updates and proactive communication at every stage." },
  { Icon: Truck,    title: "Cost-Optimized Routing",           desc: "Smart logistics planning to balance speed and cost effectively." },
];

function TickerStrip() {
  // Duplicate items so the loop scrolls seamlessly
  const items = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div
      className="overflow-hidden py-4 border-y"
      style={{ background: "#DC2A1C", borderColor: "#c42418" }}
    >
      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track {
          display: flex;
          width: max-content;
          animation: ticker-scroll 38s linear infinite;
        }
        .ticker-track:hover { animation-play-state: paused; }
      `}</style>
      <div className="ticker-track">
        {items.map((item, i) => (
          <span key={i} className="flex items-center gap-3 px-6 whitespace-nowrap text-white text-sm font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-white/60 flex-shrink-0" />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------- quick quote form ---------- */
function Input({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-gray-600 mb-2">{label}</label>
      <input {...props} data-testid={testid} className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#DC2A1C]" />
    </div>
  );
}

function QuoteForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    service: "Air Freight", details: "", contact: "Email", sales: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.details.trim()) {
      toast.error("Please fill in all required fields."); return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please enter a valid email."); return;
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        name: form.name, email: form.email, phone: form.phone,
        service: form.service, details: form.details,
        contact_method: form.contact,
        inquiry_type: form.sales ? "sales" : "quote",
      });
      setSubmitted(true);
      toast.success("Thank you! Our team will be in touch shortly.");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-10 text-center shadow-sm" data-testid="quote-success">
        <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "#DC2A1C" }} strokeWidth={1.5} />
        <h3 className="text-2xl font-bold mb-2" style={{ color: "#1A1A1A" }}>Request Received</h3>
        <p className="text-gray-600 mb-6">Our team will be in touch shortly. A confirmation has been queued to {form.email}.</p>
        <button className="tl-btn-outline" onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", service: "Air Freight", details: "", contact: "Email", sales: false }); }}>
          Submit Another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="bg-white border border-gray-100 rounded-xl p-8 md:p-10 shadow-sm space-y-5" data-testid="quote-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Input label="Full Name *" name="name" value={form.name} onChange={onChange} testid="quote-name" />
        <Input label="Email *" name="email" type="email" value={form.email} onChange={onChange} testid="quote-email" />
        <Input label="Phone Number *" name="phone" value={form.phone} onChange={onChange} testid="quote-phone" />
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-gray-600 mb-2">Service Type</label>
          <select name="service" value={form.service} onChange={onChange} className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#DC2A1C]" data-testid="quote-service">
            {SERVICES.map((s) => <option key={s.slug}>{s.title}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-gray-600 mb-2">Shipment Details *</label>
        <textarea name="details" rows="4" value={form.details} onChange={onChange} placeholder="Origin, destination, cargo type, weight, dimensions..." className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#DC2A1C]" data-testid="quote-details" />
      </div>
      <div className="flex flex-wrap gap-6 items-center">
        <div>
          <div className="block text-xs uppercase tracking-[0.15em] font-semibold text-gray-600 mb-2">Preferred Contact</div>
          <div className="flex gap-6">
            {["Email", "Phone"].map((c) => (
              <label key={c} className="flex items-center gap-2 cursor-pointer" style={{ color: "#1A1A1A" }}>
                <input type="radio" name="contact" value={c} checked={form.contact === c} onChange={onChange} className="accent-[#DC2A1C]" data-testid={`quote-contact-${c.toLowerCase()}`} />
                <span className="text-sm">{c}</span>
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm ml-auto" style={{ color: "#1A1A1A" }} data-testid="quote-sales-toggle">
          <input type="checkbox" name="sales" checked={form.sales} onChange={onChange} className="accent-[#DC2A1C]" />
          This is a sales inquiry
        </label>
      </div>
      <button type="submit" disabled={submitting} className="tl-btn w-full md:w-auto disabled:opacity-60" data-testid="quote-submit">
        {submitting ? "Submitting..." : <>Submit Request <ArrowRight className="w-4 h-4 inline-block ml-1" /></>}
      </button>
    </form>
  );
}

export default function HomePage() {
  const [heroTickerIndex, setHeroTickerIndex] = useState(0);
  const [heroTickerVisible, setHeroTickerVisible] = useState(true);
  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    api.get("/holidays/upcoming")
      .then(r => {
        const all = Array.isArray(r.data) ? r.data : [];
        const now = new Date();
        const cutoff = new Date(); cutoff.setDate(now.getDate() + 60);
        setHolidays(all.filter(h => { const d = new Date(h.date); return d >= now && d <= cutoff; }).slice(0, 6));
      })
      .catch(() => setHolidays([]));
  }, []);
  const activeTickerLogo = HERO_TICKER_LOGOS[heroTickerIndex] || HERO_TICKER_LOGOS[0];

  useEffect(() => {
    let fadeTimer;
    const timer = setInterval(() => {
      setHeroTickerVisible(false);
      fadeTimer = setTimeout(() => {
        setHeroTickerIndex((prev) => (prev + 1) % TICKER_ITEMS.length);
        setHeroTickerVisible(true);
      }, 250);
    }, 3000);

    return () => {
      clearInterval(timer);
      clearTimeout(fadeTimer);
    };
  }, []);

  return (
    <div className="App">
      <PublicNav />

      {/* Hero */}
      <section id="home" className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]" style={{ color: "#1A1A1A" }}>
              Your Logistics Partner <span style={{ color: "#DC2A1C" }}>In India</span>
            </h1>
            <div className="flex w-fit items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase mt-6" style={{ background: "rgba(220,42,28,0.1)", color: "#DC2A1C" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#DC2A1C" }} /> End to End Logistics Solutions
            </div>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed max-w-xl">
              Global logistics solutions, air freight forwarding, ocean freight forwarding, customs clearance and warehousing with seamless door-to-door delivery.
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <a href="#quote" className="tl-btn inline-flex items-center gap-2" data-testid="hero-cta">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#services" className="tl-btn-outline">Our Services</a>
            </div>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[{ k: "100+", v: "Countries" }, { k: "5000+", v: "Clients" }, { k: "99.4%", v: "On-time" }].map((s) => (
                <div key={s.k}>
                  <div className="text-3xl font-black" style={{ color: "#1A1A1A" }}>{s.k}</div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 mt-1">{s.v}</div>
                </div>
              ))}
            </div>

            {/* Certifications */}
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold mb-3">Certifications &amp; Approvals</div>
              <div className="flex flex-wrap gap-3 items-center">
                {["IATA", "MTO", "ACAAI", "AEO", "FIATA", "FFI", "SGS"].map((label) => (
                  <div
                    key={label}
                    className="px-3 py-2 rounded-xl text-sm font-black tracking-[0.12em] uppercase cursor-default select-none"
                    style={{ border: "2.5px solid #DC2A1C", color: "#DC2A1C", background: "#ffffff", transition: "background 0.25s, color 0.25s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "linear-gradient(135deg,#DC2A1C,#a81e15)"; e.currentTarget.style.color = "#ffffff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#ffffff"; e.currentTarget.style.color = "#DC2A1C"; }}
                  >
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] aspect-[4/5] md:aspect-auto md:h-[520px]">
            <img
              src="/assets/plane_wing.avif"
              alt="Cargo aircraft in flight"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-5 left-5 right-5 backdrop-blur rounded-xl p-4 flex items-center gap-4" style={{ background: "rgba(255,255,255,0.95)" }}>
              <div
                className="w-11 h-11 rounded-lg grid place-items-center text-white shadow-md ring-2 ring-white flex-shrink-0 transition-opacity duration-300"
                style={{ background: activeTickerLogo.color, opacity: heroTickerVisible ? 1 : 0 }}
                aria-label={activeTickerLogo.label}
                title={activeTickerLogo.label}
              >
                <activeTickerLogo.Icon size={18} />
              </div>
              <div style={{ opacity: heroTickerVisible ? 1 : 0, transition: "opacity 0.3s" }}>
                <div className="text-sm font-semibold leading-snug" style={{ color: "#1A1A1A" }}>
                  {CAPABILITIES[heroTickerIndex]?.title}
                </div>
                <div className="text-xs mt-0.5 leading-snug" style={{ color: "#6b7280" }}>
                  {CAPABILITIES[heroTickerIndex]?.desc}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities ticker strip */}
      <TickerStrip />

      {/* Flight image strip */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-3 h-48 md:h-64">
        <div className="overflow-hidden">
          <img src="https://images.unsplash.com/photo-1570710891163-6d3b5c47248b?auto=format&fit=crop&w=700&q=80"
            alt="Cargo aircraft loading" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
        </div>
        <div className="overflow-hidden hidden sm:block">
          <img src="https://images.unsplash.com/photo-1474302770737-173ee21bab63?auto=format&fit=crop&w=700&q=80"
            alt="Aircraft engines" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
        </div>
        <div className="overflow-hidden hidden sm:block">
          <img src="https://images.unsplash.com/photo-1556388158-158ea5ccacbd?auto=format&fit=crop&w=700&q=80"
            alt="Air freight terminal" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
        </div>
      </div> */}

      {/* Services */}
      <section id="services" className="py-12 md:py-20 px-6 md:px-12" style={{ background: "#F8F9FA" }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#DC2A1C" }}>Our Services</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight" style={{ color: "#1A1A1A" }}>One partner. Multi mode of transport.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <Link to={`/services/${s.slug}`} key={s.slug}
                className="group bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300 block"
                data-testid={`service-card-${s.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="w-10 h-10 rounded-lg grid place-items-center mb-3" style={{ background: "rgba(220,42,28,0.1)" }}>
                    <s.icon className="w-5 h-5" style={{ color: "#DC2A1C" }} strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-semibold mb-1.5 leading-snug" style={{ color: "#1A1A1A" }}>{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3 line-clamp-3">{s.short}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all" style={{ color: "#DC2A1C" }}>
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-12 md:py-20 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-start">
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl overflow-hidden aspect-[16/9]">
              <img src="/assets/aircargo1.jfif" alt="Our team" className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img src="/assets/3.png" alt="Cargo aircraft" className="w-full h-full object-cover" />
              </div>
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img src="/assets/2.png" alt="Freight operations" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#DC2A1C" }}>About Us</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-5" style={{ color: "#1A1A1A" }}>Engineered for Global Logistics.</h2>

            <div className="space-y-4 text-gray-600 leading-relaxed text-sm">
              <p>
                Established in 2009, Translog Express was founded by a team of young professionals backed by over 15 years of experience in the logistics industry. With a clear vision to deliver expertise-driven and value-added services, we aim to provide complete logistics solutions under one roof.
              </p>
              <p>
                We are committed to punctuality, transparency and customer satisfaction. By understanding the unique logistics requirements of each client, we deliver simple, efficient and reliable solutions that create true win-win outcomes.
              </p>
              <p>
                Our services include comprehensive door-to-door and customized logistics solutions for both import and export cargo. We handle a wide range of commodities with precision and care, supported by a strong network of global agents and trusted partners including airlines, shipping lines and international associates.
              </p>
              <p>
                Our strength lies in our business ethics, competitive pricing and consistent service quality. At Translog Express, our team of experienced professionals is driven by one core principle: to understand client needs thoroughly and deliver the most effective and straightforward solutions, often exceeding expectations.
              </p>
              <p>
                With a robust global presence and a customer-first approach, we continue to build long-term partnerships and deliver logistics solutions you can rely on.
              </p>
            </div>

            {/* Credentials card */}
            <div className="mt-6 rounded-xl p-5 border" style={{ background: "#F8F9FA", borderColor: "#e5e7eb" }}>
              <div className="text-xs uppercase tracking-[0.15em] font-semibold mb-3" style={{ color: "#DC2A1C" }}>Credentials</div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Translog Express is an IATA-approved company and an authorized Multimodal Transport Operator (MTO), approved by the Directorate General of Shipping, Government of India. Established on 1st October 2009, the company has grown to achieve an annual turnover of approximately USD 20 million with consistent year-on-year growth of 10 to 13%. We operate across all major locations in India with company-owned offices in Mumbai, Ahmedabad, Kolkata, Pune, Mundra and Hyderabad.
              </p>
            </div>

            <div className="mt-6">
              <a href="#quote" className="tl-btn inline-flex items-center gap-2" data-testid="about-quote-link">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us — Aircraft Feature Strip */}
      <section className="relative overflow-hidden">
        <img
          src="/assets/aircargo_wide.jfif"
          alt="Cargo aircraft on tarmac"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, rgba(26,26,26,0.93) 0%, rgba(26,26,26,0.80) 55%, rgba(26,26,26,0.60) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#DC2A1C" }}>Why Translog Express</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight mb-4">
              Built for speed.<br />Trusted globally.
            </h2>
            <p className="text-gray-400 leading-relaxed mb-6 max-w-sm">
              From single-parcel express to full freighter charters, we move cargo that matters on time every time.
            </p>
            <a href="#quote" className="tl-btn inline-flex items-center gap-2">
              Get a Quote <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { icon: Plane,    title: "Air Freight Priority",      desc: "Same-day processing for time-critical shipments." },
              { icon: Globe,    title: "100+ Countries",            desc: "Unmatched global agent network for door-to-door worldwide coverage." },
              { icon: Shield,   title: "Specialised Cargo Experts", desc: "Safe handling of DG cargo, valuable shipments, ODC, and time sensitive freight." },
              { icon: FileText, title: "Hassle Free Documentation", desc: "Reliable support for import, export, customs, and compliance documentation." },
            ].map((f) => (
              <div key={f.title} className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl flex-shrink-0 grid place-items-center" style={{ background: "rgba(220,42,28,0.18)" }}>
                  <f.icon className="w-5 h-5" style={{ color: "#DC2A1C" }} strokeWidth={2} />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm mb-1">{f.title}</div>
                  <div className="text-gray-400 text-xs leading-relaxed">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Footprint Map */}
      <section className="py-12 md:py-20 px-6 md:px-12" style={{ background: "#F8F9FA" }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-8">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#DC2A1C" }}>Our Global Footprint</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight" style={{ color: "#1A1A1A" }}>
              100+ countries. One trusted partner.
            </h2>
            <p className="mt-3 text-gray-600 text-sm leading-relaxed">
              A worldwide network of trusted agents enabling seamless import, export, and door-to-door cargo solutions.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
            <GlobalFootprintMap />
          </div>
        </div>
      </section>

      {/* Upcoming Holidays */}
      <section className="py-10 md:py-14 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "rgba(220,42,28,0.1)" }}>
              <CalendarDays className="w-4 h-4" style={{ color: "#DC2A1C" }} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "#DC2A1C" }}>Holiday Schedule</div>
              <div className="text-sm font-bold" style={{ color: "#1A1A1A" }}>Upcoming Holidays</div>
            </div>
          </div>
          {holidays.length === 0 ? (
            <p className="text-sm text-gray-400">No upcoming holidays in the next 60 days.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {holidays.map((h) => (
                <div key={h.id} className="rounded-xl border border-gray-100 p-3 text-center shadow-sm" style={{ background: "#F8F9FA" }}>
                  <div className="w-10 h-10 rounded-lg text-white grid place-items-center mx-auto mb-2" style={{ background: "#DC2A1C" }}>
                    <div className="leading-none">
                      <div className="text-sm font-black">{new Date(h.date).getDate()}</div>
                      <div className="text-[8px] uppercase tracking-wider">{new Date(h.date).toLocaleString("en", { month: "short" })}</div>
                    </div>
                  </div>
                  <div className="text-xs font-semibold leading-tight" style={{ color: "#1A1A1A" }}>{h.title}</div>
                  {h.region && <div className="text-[10px] text-gray-400 mt-0.5">{h.region}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Quote CTA */}
      <section id="quote" className="py-12 md:py-20 px-6 md:px-12" style={{ background: "#1A1A1A" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#DC2A1C" }}>Request a Quote</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3 text-white">Ready to ship smarter?</h2>
          <p className="text-gray-400 mb-6 max-w-2xl">Share a few details and our logistics team will get back to you with an all-inclusive quote.</p>
          <p className="text-gray-400 mb-8 text-sm">
            Prefer a direct line? Call{" "}
            <a href={`tel:${CONTACT.phoneHref}`} className="text-white hover:underline" data-testid="hero-phone">{CONTACT.phone}</a>
            {" "}or email{" "}
            <a href={`mailto:${CONTACT.email}`} className="text-white hover:underline break-all" data-testid="hero-email">{CONTACT.email}</a>.
          </p>
          <div className="w-full max-w-md mx-auto">
            <QuickInquiryForm compact={false} />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
